import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { logError } from "@/lib/logger";
import { FeatureGate } from "@/lib/features";
import { ShieldWarning, UserCircle, Palette, Globe, CheckCircle, WarningCircle, ClipboardText, ArrowRight } from "@phosphor-icons/react";
import SidebarOrderEditor from "@/components/SidebarOrderEditor";

export default function WorkspaceSettings() {
    const [settings, setSettings] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/workspace/settings")
            .then((r) => setSettings(r.data || {}))
            .catch((e) => {
                logError("workspace settings load failed", e);
                setSettings({});
            });
    }, []);

    const toggleAvatars = async (next) => {
        setBusy(true);
        try {
            const r = await api.patch("/workspace/settings", {
                show_candidate_avatars_in_list: next,
            });
            setSettings(r.data || {});
            toast.success(
                next
                    ? "Candidate photos are now visible in the list view."
                    : "Candidate photos are hidden from the list view.",
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't update setting.");
        } finally {
            setBusy(false);
        }
    };

    // V1.4 — Awaiting-reg threshold input (days). Debounced write.
    const [thresholdDraft, setThresholdDraft] = useState("");
    useEffect(() => {
        if (settings) {
            setThresholdDraft(String(settings.awaiting_reg_threshold_days ?? 3));
        }
    }, [settings]);
    const saveThreshold = async () => {
        const n = parseInt(thresholdDraft, 10);
        if (!Number.isFinite(n) || n < 1 || n > 30) {
            toast.error("Threshold must be between 1 and 30 days.");
            return;
        }
        setBusy(true);
        try {
            const r = await api.patch("/workspace/settings", {
                awaiting_reg_threshold_days: n,
            });
            setSettings(r.data || {});
            toast.success(`"Awaiting reg" filter now triggers at ${n} day${n === 1 ? "" : "s"}.`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save threshold.");
        } finally {
            setBusy(false);
        }
    };

    if (!settings) {
        return (
            <div className="px-4 sm:px-8 py-10 text-[#525252]" data-testid="workspace-settings-loading">
                Loading…
            </div>
        );
    }

    const avatarsOn = !!settings.show_candidate_avatars_in_list;
    // V2.0.44 — Recruiter full-workspace visibility toggle.
    const fullVisibilityOn = !!settings.recruiter_full_visibility;
    const toggleFullVisibility = async (next) => {
        setBusy(true);
        try {
            const r = await api.patch("/workspace/settings", {
                recruiter_full_visibility: next,
            });
            setSettings(r.data || {});
            toast.success(
                next
                    ? "Recruiters can now see every candidate in this workspace."
                    : "Recruiters are back to seeing only their own + team-pool candidates.",
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't update setting.");
        } finally { setBusy(false); }
    };

    return (
        <div
            data-testid="workspace-settings-page"
            className="px-4 sm:px-8 py-6 sm:py-10 max-w-3xl mx-auto"
        >
            <div className="mb-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                    Admin only · workspace preferences
                </p>
                <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-ink">
                    Workspace settings.
                </h1>
                <p className="text-sm text-[#525252] mt-2 max-w-xl">
                    These preferences apply to every recruiter and hiring manager in your workspace.
                    Change them thoughtfully — some settings affect hiring fairness.
                </p>
            </div>

            <Link
                to="/settings/screening-intake"
                data-testid="setting-intake-template-link"
                className="group block bg-white border border-[#E5E5E5] hover:border-cobalt p-5 sm:p-6 mb-5 transition-colors"
            >
                <div className="flex items-start gap-3">
                    <ClipboardText
                        size={22}
                        weight="bold"
                        className="text-cobalt mt-0.5 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h2 className="font-heading text-lg font-black tracking-tight text-ink group-hover:text-cobalt transition-colors">
                                    Screening intake fields
                                </h2>
                                <p className="text-sm text-[#525252] mt-1">
                                    Add, edit, reorder, and mark-required the intake questions asked
                                    of every candidate in this workspace. Blocks advance to Interview
                                    until every required field is filled.
                                </p>
                            </div>
                            <ArrowRight size={18} weight="bold"
                                        className="text-[#A3A3A3] group-hover:text-cobalt transition-colors shrink-0 mt-1" />
                        </div>
                    </div>
                </div>
            </Link>

            <Link
                to="/settings/daddytalk"
                data-testid="setting-daddytalk-link"
                className="group block bg-white border border-[#E5E5E5] hover:border-cobalt p-5 sm:p-6 mb-5 transition-colors"
            >
                <div className="flex items-start gap-3">
                    <img
                        src="/mascot/daddytalk/avatar_hello.png"
                        alt=""
                        className="w-8 h-8 rounded-full shrink-0 mt-0.5 border border-[#E5E5E5]"
                    />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h2 className="font-heading text-lg font-black tracking-tight text-ink group-hover:text-cobalt transition-colors">
                                    DaddyTalk — Direct chat controls
                                </h2>
                                <p className="text-sm text-[#525252] mt-1">
                                    Enable DaddyTalk for this workspace, flip which recruiters are
                                    visible in the candidate directory, and set their concurrent
                                    chat capacity. Master switch is off by default.
                                </p>
                            </div>
                            <ArrowRight size={18} weight="bold"
                                        className="text-[#A3A3A3] group-hover:text-cobalt transition-colors shrink-0 mt-1" />
                        </div>
                    </div>
                </div>
            </Link>

            {/* V1.5.8.27 — admin-editable sidebar order. Workspace-wide,
                admin-only edit; applied for recruiter/HM/admin. */}
            <SidebarOrderEditor />

            <section
                data-testid="setting-candidate-avatars"
                className="bg-white border border-[#E5E5E5] p-5 sm:p-6 mb-5"
            >
                <div className="flex items-start gap-3">
                    <UserCircle
                        size={22}
                        weight="bold"
                        className="text-cobalt mt-0.5 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h2 className="font-heading text-lg font-black tracking-tight text-ink">
                                    Show candidate photos in the list view
                                </h2>
                                <p className="text-xs text-[#525252] mt-1 leading-relaxed max-w-xl">
                                    When off, photos only appear inside the 360° profile panel. This
                                    is the recommended default — visible photos in list views have
                                    been shown to increase unconscious bias in shortlisting.
                                </p>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={avatarsOn}
                                data-testid="toggle-candidate-avatars"
                                disabled={busy}
                                onClick={() => toggleAvatars(!avatarsOn)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${
                                    avatarsOn ? "bg-cobalt" : "bg-[#E5E5E5]"
                                } ${busy ? "opacity-50" : "hover:opacity-90"}`}
                            >
                                <span
                                    className={`inline-block h-5 w-5 bg-white rounded-full shadow transition-transform ${
                                        avatarsOn ? "translate-x-5" : "translate-x-0.5"
                                    }`}
                                />
                            </button>
                        </div>

                        {avatarsOn && (
                            <div
                                data-testid="avatar-bias-warning"
                                className="mt-3 flex items-start gap-2 border border-[#FEF3C7] bg-[#FFFBEB] p-3"
                            >
                                <ShieldWarning
                                    size={16}
                                    weight="fill"
                                    className="text-[#B45309] mt-0.5 shrink-0"
                                />
                                <p className="text-[11px] text-[#B45309] leading-relaxed">
                                    Photos are now visible in the list view. Please train your team
                                    on structured shortlisting to keep bias in check.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* V2.0.44 — Recruiter full-workspace visibility toggle. */}
            <section className="mb-6 border border-[#E5E5E5] bg-white p-5 sm:p-6">
                <div className="flex items-start gap-4">
                    <Globe
                        size={20}
                        weight="bold"
                        className="text-cobalt mt-0.5 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h2 className="font-heading text-lg font-black tracking-tight text-ink">
                                    Recruiter sees the whole workspace
                                </h2>
                                <p className="text-xs text-[#525252] mt-1 leading-relaxed max-w-xl">
                                    When ON, every recruiter can <strong>search and open any candidate&apos;s profile</strong> in this workspace &mdash; name, contact, CV, skills, experience.
                                    <br /><br />
                                    <strong>Pipeline data stays private per owner.</strong> Each recruiter&apos;s Kanban shows only their own + team-pool candidates. Foreign candidates open in a &ldquo;profile only&rdquo; view &mdash; no stage, notes, activity, tags, or interview history.
                                    <br /><br />
                                    Reassignment: recruiters can only hand off candidates they own; admins can reassign anyone.
                                </p>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={fullVisibilityOn}
                                data-testid="toggle-recruiter-full-visibility"
                                disabled={busy}
                                onClick={() => toggleFullVisibility(!fullVisibilityOn)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${
                                    fullVisibilityOn ? "bg-cobalt" : "bg-[#E5E5E5]"
                                } ${busy ? "opacity-50" : "hover:opacity-90"}`}
                            >
                                <span
                                    className={`inline-block h-5 w-5 bg-white rounded-full shadow transition-transform ${
                                        fullVisibilityOn ? "translate-x-5" : "translate-x-0.5"
                                    }`}
                                />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* V1.4 — Awaiting-reg threshold */}
            <section
                data-testid="setting-awaiting-reg-threshold"
                className="bg-white border border-[#E5E5E5] p-5 sm:p-6 mb-5"
            >
                <div className="flex items-start gap-3">
                    <UserCircle
                        size={22}
                        weight="bold"
                        className="text-amber-600 mt-0.5 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                        <h2 className="font-heading text-lg font-black tracking-tight text-ink">
                            &quot;Awaiting reg&quot; filter threshold
                        </h2>
                        <p className="text-xs text-[#525252] mt-1 leading-relaxed max-w-xl">
                            How many days a candidate has to sit without registering
                            before the Kanban filter chip surfaces them. Aggressive teams
                            catch stalls at 1 day; ops-heavy teams stay at 7. Range: 1–30 days.
                        </p>
                        <div className="mt-4 flex items-center gap-3">
                            <input
                                type="number"
                                min="1"
                                max="30"
                                value={thresholdDraft}
                                onChange={(e) => setThresholdDraft(e.target.value)}
                                data-testid="input-awaiting-reg-threshold"
                                disabled={busy}
                                className="w-24 border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-ink"
                            />
                            <span className="text-xs text-[#525252]">days</span>
                            <button
                                type="button"
                                onClick={saveThreshold}
                                disabled={busy || thresholdDraft === String(settings.awaiting_reg_threshold_days ?? 3)}
                                data-testid="btn-save-awaiting-reg-threshold"
                                className="bg-ink hover:bg-cobalt disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-2 text-xs font-black uppercase tracking-wider transition-colors"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Branding — Enterprise feature */}
            <section data-testid="setting-branding-section" className="mt-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-3">
                    Branding
                </p>
                <FeatureGate feature="custom_branding" upgradeLabel="Enterprise plan required">
                    <BrandingEditor />
                </FeatureGate>
            </section>

            {/* Custom Domain — Enterprise feature */}
            <section data-testid="setting-custom-domain-section" className="mt-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-3">
                    Custom domain
                </p>
                <FeatureGate feature="custom_domain" upgradeLabel="Enterprise plan required">
                    <CustomDomainEditor />
                </FeatureGate>
            </section>

            {/* V1.1 #12 — Interview scheduling preferences */}
            <section data-testid="setting-calendar-section" className="mt-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-3">
                    Interview calendar
                </p>
                <CalendarSyncToggle />
            </section>
        </div>
    );
}

// ---------- Google Calendar sync toggle (V1.1 #12) ----------
function CalendarSyncToggle() {
    const [enabled, setEnabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/settings/calendar")
            .then((r) => setEnabled(!!r.data.google_calendar_sync_enabled))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const toggle = async () => {
        const next = !enabled;
        setSaving(true);
        try {
            await api.put("/settings/calendar", { google_calendar_sync_enabled: next });
            setEnabled(next);
            toast.success(next
                ? "Google Calendar sync enabled. Recruiters who connect their Google account will get two-way sync."
                : "Reverted to native scheduling. Interviews will use ICS + Jitsi Meet — no Google connection required.");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update setting.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <p className="text-xs text-[#A3A3A3]">Loading…</p>;

    return (
        <div className="bg-white border border-[#E5E5E5] p-4 sm:p-5" data-testid="calendar-sync-toggle">
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <p className="text-sm font-bold text-ink">Enable Google Calendar sync</p>
                    <p className="text-xs text-[#525252] mt-1 leading-relaxed">
                        Off (default) — every scheduled interview is sent as an
                        <strong> .ics calendar invite</strong> + auto-generated
                        <strong> Jitsi Meet link</strong>. Works with any email provider,
                        no OAuth. On — recruiters who have connected Google will also get
                        events pushed into their personal Google Calendar with two-way sync.
                    </p>
                </div>
                <button
                    onClick={toggle}
                    disabled={saving}
                    data-testid="calendar-sync-toggle-btn"
                    aria-pressed={enabled}
                    className={`shrink-0 relative inline-flex h-6 w-11 items-center transition-colors ${enabled ? "bg-cobalt" : "bg-[#E5E5E5]"} ${saving ? "opacity-60" : ""}`}
                >
                    <span
                        className={`inline-block h-4 w-4 bg-white transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`}
                    />
                </button>
            </div>
        </div>
    );
}

// ---------- Branding Editor (Enterprise-gated) ----------
function BrandingEditor() {
    const [ws, setWs] = useState(null);
    const [form, setForm] = useState({ logo_url: "", primary_color: "#0A0A0A", brand_font: "" });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/workspaces/current").then((r) => {
            setWs(r.data);
            setForm({
                logo_url: r.data?.settings?.logo_url || "",
                primary_color: r.data?.settings?.primary_color || "#0A0A0A",
                brand_font: r.data?.settings?.brand_font || "",
            });
        }).catch(() => {});
    }, []);

    const save = async (e) => {
        e?.preventDefault();
        const body = {};
        if (form.logo_url.trim()) body.logo_url = form.logo_url.trim();
        if (/^#[0-9A-Fa-f]{6}$/.test(form.primary_color)) body.primary_color = form.primary_color;
        if (form.brand_font.trim()) body.brand_font = form.brand_font.trim();
        if (!Object.keys(body).length) { toast.info("Nothing to save."); return; }
        setSaving(true);
        try {
            await api.patch("/workspaces/current/settings", body);
            toast.success("Branding updated. Refresh to see it in the sidebar.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    if (!ws) return <div className="bg-white border border-[#E5E5E5] p-5 text-sm text-[#A3A3A3]">Loading…</div>;

    return (
        <form onSubmit={save} data-testid="branding-editor-form"
              className="bg-white border border-[#E5E5E5] p-5 sm:p-6 space-y-4">
            <div className="flex items-start gap-3">
                <Palette size={22} weight="bold" className="text-cobalt mt-0.5 shrink-0" />
                <div>
                    <h2 className="font-heading text-lg font-black tracking-tight text-ink">
                        Your workspace look &amp; feel
                    </h2>
                    <p className="text-xs text-[#525252] mt-1">
                        Applied to the sidebar for every user in your workspace.
                    </p>
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1.5">Logo URL</label>
                    <input value={form.logo_url}
                           onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                           data-testid="branding-logo-url"
                           placeholder="https://cdn.acme.com/logo.png"
                           className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-ink" />
                </div>
                <div>
                    <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1.5">Primary color</label>
                    <input value={form.primary_color}
                           onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                           data-testid="branding-primary-color"
                           placeholder="#0A0A0A"
                           className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-ink font-mono" />
                </div>
                <div className="sm:col-span-2">
                    <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1.5">Brand font</label>
                    <input value={form.brand_font}
                           onChange={(e) => setForm({ ...form, brand_font: e.target.value })}
                           data-testid="branding-font"
                           placeholder="Space Grotesk"
                           className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-ink" />
                </div>
            </div>
            {form.logo_url && (
                <div className="flex items-center gap-3 border border-[#E5E5E5] p-3">
                    <span className="text-[10px] font-black uppercase text-[#A3A3A3]">Preview:</span>
                    <img src={form.logo_url} alt="Logo preview" className="h-6 w-auto"
                         onError={(e) => { e.currentTarget.style.opacity = 0.3; }} />
                    <span data-testid="branding-color-swatch"
                          className="h-6 w-6 border border-[#E5E5E5]"
                          style={{ backgroundColor: form.primary_color }} />
                </div>
            )}
            <div className="flex justify-end">
                <button type="submit" disabled={saving} data-testid="branding-save-btn"
                        className="btn-gradient px-5 py-2.5 text-xs font-black uppercase tracking-wider disabled:opacity-60">
                    {saving ? "Saving…" : "Save branding"}
                </button>
            </div>
        </form>
    );
}

// ---------- Custom Domain Editor (Enterprise-gated) ----------
function CustomDomainEditor() {
    const [state, setState] = useState(null);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/workspaces/current/custom-domain");
            setState(r.data);
            setInput(r.data?.domain || "");
        } catch (e) {
            logError("custom domain load failed", e);
            setState({ status: "none" });
        }
    };
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e?.preventDefault();
        const d = (input || "").trim().toLowerCase();
        if (!d) { toast.error("Enter a domain."); return; }
        setBusy(true);
        try {
            await api.post("/workspaces/current/custom-domain", { domain: d });
            toast.success("Domain registered. Now add the DNS records below.");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally { setBusy(false); }
    };

    const verify = async () => {
        setBusy(true);
        try {
            const r = await api.post("/workspaces/current/custom-domain/verify");
            if (r.data.status === "verified") {
                toast.success("Domain verified! Your custom URL is live.");
            } else {
                toast.error(r.data.error || "DNS records not found yet. Wait a few minutes and try again.");
            }
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Verification failed");
        } finally { setBusy(false); }
    };

    const remove = async () => {
        if (!window.confirm("Remove the custom domain? Your public pages will fall back to the platform URL.")) return;
        setBusy(true);
        try {
            await api.delete("/workspaces/current/custom-domain");
            toast.success("Custom domain removed.");
            setInput("");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        } finally { setBusy(false); }
    };

    if (!state) return <div className="bg-white border border-[#E5E5E5] p-5 text-sm text-[#A3A3A3]">Loading…</div>;

    const s = state.status || "none";
    const hasDomain = !!state.domain;
    let statusPill = null;
    if (s === "verified") {
        statusPill = (
            <span data-testid="custom-domain-status-verified" className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5">
                <CheckCircle size={12} weight="fill" /> Verified
            </span>
        );
    } else if (s === "pending") {
        statusPill = (
            <span data-testid="custom-domain-status-pending" className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5">
                <WarningCircle size={12} weight="fill" /> Pending DNS
            </span>
        );
    }

    return (
        <form onSubmit={save} data-testid="custom-domain-editor"
              className="bg-white border border-[#E5E5E5] p-5 sm:p-6 space-y-5">
            <div className="flex items-start gap-3">
                <Globe size={22} weight="bold" className="text-cobalt mt-0.5 shrink-0" />
                <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="font-heading text-lg font-black tracking-tight text-ink">
                            Bring your own domain
                        </h2>
                        {statusPill}
                    </div>
                    <p className="text-xs text-[#525252] mt-1 max-w-xl">
                        Point <code>careers.yourcompany.com</code> at NaukriDaddy and serve your
                        public career pages under your own brand. Admin traffic stays on the platform URL.
                    </p>
                </div>
            </div>

            <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1.5">
                    Your domain
                </label>
                <div className="flex gap-2">
                    <input value={input}
                           onChange={(e) => setInput(e.target.value)}
                           data-testid="custom-domain-input"
                           disabled={busy || (hasDomain && s === "verified")}
                           placeholder="careers.acme.com"
                           className="flex-1 border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-ink disabled:bg-[#FAFAFA]" />
                    {!hasDomain && (
                        <button type="submit" disabled={busy}
                                data-testid="custom-domain-register-btn"
                                className="btn-gradient px-4 py-2 text-xs font-black uppercase tracking-wider disabled:opacity-60">
                            Register
                        </button>
                    )}
                </div>
            </div>

            {hasDomain && (
                <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-4 space-y-3">
                    <p className="text-[10px] font-black uppercase tracking-wider text-[#525252]">
                        Add these two records at your DNS provider
                    </p>
                    <div className="space-y-2 font-mono text-xs">
                        <div className="grid grid-cols-[80px_1fr] gap-3">
                            <span className="text-[#A3A3A3]">Type</span>
                            <span className="text-ink">CNAME</span>
                            <span className="text-[#A3A3A3]">Name</span>
                            <span className="text-ink break-all" data-testid="custom-domain-cname-name">{state.domain}</span>
                            <span className="text-[#A3A3A3]">Value</span>
                            <span className="text-ink break-all" data-testid="custom-domain-cname-target">{state.cname_target}</span>
                        </div>
                        <hr className="border-[#E5E5E5]" />
                        <div className="grid grid-cols-[80px_1fr] gap-3">
                            <span className="text-[#A3A3A3]">Type</span>
                            <span className="text-ink">TXT</span>
                            <span className="text-[#A3A3A3]">Name</span>
                            <span className="text-ink break-all" data-testid="custom-domain-txt-name">{state.txt_record_name}</span>
                            <span className="text-[#A3A3A3]">Value</span>
                            <span className="text-ink break-all" data-testid="custom-domain-txt-value">{state.verification_token}</span>
                        </div>
                    </div>
                    {state.last_error && s !== "verified" && (
                        <p className="text-[11px] text-amber-700 border-t border-[#E5E5E5] pt-2">
                            {state.last_error}
                        </p>
                    )}
                    <div className="flex gap-2 pt-1">
                        {s !== "verified" && (
                            <button type="button" onClick={verify} disabled={busy}
                                    data-testid="custom-domain-verify-btn"
                                    className="px-4 py-2 text-xs font-black uppercase tracking-wider border border-ink bg-ink text-white hover:opacity-90 disabled:opacity-60">
                                {busy ? "Checking DNS…" : "Verify now"}
                            </button>
                        )}
                        <button type="button" onClick={remove} disabled={busy}
                                data-testid="custom-domain-remove-btn"
                                className="px-4 py-2 text-xs font-black uppercase tracking-wider border border-[#E5E5E5] text-[#525252] hover:border-ink hover:text-ink disabled:opacity-60">
                            Remove
                        </button>
                    </div>
                </div>
            )}
        </form>
    );
}

